import { normalizeOrganizedTourSearchResult } from "./normalizeOrganizedTourSearchResult";
import { buildOrganizedTourProfileViewModel } from "./buildOrganizedTourProfileViewModel";
import {
  extractOrganizedTourFlightLegs,
  formatOrganizedTourFlightDateTime,
} from "./organizedTourProfileFlights";
import {
  resolveOrgTourInfoDisplay,
  getOrgTourInfoDangerousInnerHtml,
  getOrgTourInfoPlainTextForDisplay,
  orgTourInfoContentLooksLikeRawSource,
} from "./orgTourInfoDisplay";
import { resolveOrganizedTourDisplaySummary } from "./resolveOrganizedTourDisplaySummary";

export { ORG_TOUR_ITINERARY_SOFT_NOTICE_HE } from "./orgTourInfoDisplay";

/** מחיקת תגים ו־CSS מוטמע — טקסט קריא בלבד (לא מזריקים HTML חדש). */
export function stripSupplierHtmlToReadableText(html, maxLen = 12000) {
  if (html == null || typeof html !== "string") return "";
  const slice = html.length > maxLen ? html.slice(0, maxLen) : html;
  return slice
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|tr|li|h[1-6])\s*>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/[ \t\f\v]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]+/g, " ")
    .trim();
}

function nightsFromOrderResponse(savedOrder) {
  const h = savedOrder?.order_response?.reservation?.hotel;
  if (!h || typeof h !== "object") return null;
  const n = h.nights ?? h.Nights ?? h.numberOfNights;
  return n != null && Number.isFinite(Number(n)) && Number(n) > 0 ? Number(n) : null;
}

function nightsFromSelectedHotel(selectedResult) {
  const h = selectedResult?.hotel;
  if (!h || typeof h !== "object") return null;
  const n = h.nights ?? h.nightsCount ?? h.numberOfNights;
  return n != null && Number.isFinite(Number(n)) && Number(n) > 0 ? Number(n) : null;
}

function buildFlightLines(selectedResult) {
  const legs = extractOrganizedTourFlightLegs(selectedResult?.linkedFlights);
  return legs.map((leg) => {
    const dep = formatOrganizedTourFlightDateTime(leg.depDate);
    const from = leg.depCity || "";
    const to = leg.arrCity || "";
    const line = [from && to ? `${from} → ${to}` : "", dep.date, dep.time]
      .filter(Boolean)
      .join(" · ");
    return {
      segmentLabel: String(leg.segmentLabel || "טיסה"),
      line: line || "—",
    };
  });
}

/**
 * סיכום מסלול / טיסות כש־tourStructure או HTML ספק אינם זמינים — תמיד מחזיר שורות מובנות כשיש selectedResult.
 *
 * @param {{
 *   selectedResult?: object|null,
 *   savedOrder?: object|null,
 *   orgTourInfo?: { contentText?: string, contentHtmlSanitized?: string, resourceUrl?: string }|null,
 * }} input
 * @returns {{
 *   structuredLines: Array<{ key: string, label: string, value: string }>,
 *   narrativeHtml: string|null,
 *   narrativeText: string|null,
 *   showSoftNotice: boolean,
 *   sources: string[],
 * }}
 */
export function resolveOrganizedTourItineraryFallback(input) {
  const selectedResult = input?.selectedResult && typeof input.selectedResult === "object"
    ? input.selectedResult
    : null;
  const savedOrder = input?.savedOrder && typeof input.savedOrder === "object" ? input.savedOrder : null;
  const orgTourInfo = input?.orgTourInfo && typeof input.orgTourInfo === "object" ? input.orgTourInfo : {};

  const sources = [];
  const structuredLines = [];

  const rawHtml = String(orgTourInfo.contentHtmlSanitized ?? "").trim();
  const rawText = String(orgTourInfo.contentText ?? "").trim();

  let norm = null;
  let profileVm = null;
  if (selectedResult) {
    norm = normalizeOrganizedTourSearchResult(selectedResult);
    sources.push("selectedResult.normalize");
    profileVm = buildOrganizedTourProfileViewModel({
      selectedResult,
      resourceUrl: orgTourInfo.resourceUrl ?? null,
      ourPrice: null,
      savedOrder,
    });
    sources.push("buildOrganizedTourProfileViewModel");
  }

  const orderSummary =
    savedOrder ? resolveOrganizedTourDisplaySummary(savedOrder, { silent: true }) : null;
  if (orderSummary) sources.push("resolveOrganizedTourDisplaySummary");

  const tourName =
    orderSummary?.tourNameSource !== "fallback" && String(orderSummary?.tourName || "").trim()
      ? String(orderSummary.tourName).trim()
      : norm?.tourName || profileVm?.tourName || "";

  const routeLabel =
    orderSummary?.routeSource !== "empty" && String(orderSummary?.routeLabel || "").trim()
      ? String(orderSummary.routeLabel).trim()
      : String(norm?.routeLabel || profileVm?.routeLabel || "").trim();

  if (tourName) {
    structuredLines.push({ key: "title", label: "שם הטיול", value: tourName });
  }
  if (routeLabel) {
    structuredLines.push({ key: "route", label: "יעדים וסדר מסלול", value: routeLabel });
  }

  const dateDisp =
    norm?.dateRangeDisplay ||
    (profileVm?.depDateLabel && profileVm?.retDateLabel
      ? `${profileVm.depDateLabel} — ${profileVm.retDateLabel}`
      : "");
  if (dateDisp) {
    structuredLines.push({ key: "dates", label: "תאריכים", value: dateDisp });
  }

  const nights =
    norm?.nights ??
    nightsFromOrderResponse(savedOrder) ??
    nightsFromSelectedHotel(selectedResult) ??
    null;
  if (nights != null && Number.isFinite(Number(nights)) && Number(nights) > 0) {
    structuredLines.push({ key: "nights", label: "לילות", value: String(Math.round(Number(nights))) });
    sources.push("nights");
  }

  const flightLines = selectedResult ? buildFlightLines(selectedResult) : [];
  flightLines.forEach((fl, idx) => {
    structuredLines.push({
      key: `flight-${idx}`,
      label: fl.segmentLabel,
      value: fl.line,
    });
    if (idx === 0) sources.push("linkedFlights");
  });

  const disp = resolveOrgTourInfoDisplay({
    contentHtmlSanitized: rawHtml || null,
    contentText: rawText || null,
  });

  let narrativeHtml = disp.mode === "html" ? getOrgTourInfoDangerousInnerHtml(disp) : null;
  let narrativeText = disp.mode === "text" ? getOrgTourInfoPlainTextForDisplay(disp) : null;

  if (!narrativeHtml && !narrativeText && rawHtml.length > 0) {
    const recovered = stripSupplierHtmlToReadableText(rawHtml);
    if (recovered.length >= 40 && !orgTourInfoContentLooksLikeRawSource(recovered)) {
      narrativeText = recovered;
      // eslint-disable-next-line no-console
      console.info("[ORG_TOUR_ITINERARY_TEXT_FALLBACK]", {
        recoveredLen: recovered.length,
      });
    } else if (rawHtml.length > 80) {
      // eslint-disable-next-line no-console
      console.info("[ORG_TOUR_ITINERARY_HTML_INVALID]", {
        reason: recovered.length < 40 ? "stripped_too_short" : "still_raw_like",
        rawLen: rawHtml.length,
      });
    }
  }

  if (disp.mode === "none" && rawHtml && orgTourInfoContentLooksLikeRawSource(rawHtml)) {
    // eslint-disable-next-line no-console
    console.info("[ORG_TOUR_ITINERARY_HTML_INVALID]", { reason: "blocked_raw_like_html" });
  }

  const hasNarrative = Boolean(
    (narrativeHtml && narrativeHtml.trim().length > 0) ||
      (narrativeText && narrativeText.trim().length > 0)
  );
  const showSoftNotice =
    !hasNarrative && (structuredLines.length > 0 || Boolean(selectedResult));

  // eslint-disable-next-line no-console
  console.info("[ORG_TOUR_ITINERARY_FALLBACK_BUILD]", {
    structuredCount: structuredLines.length,
    hasNarrative,
    showSoftNotice,
    sources: sources.length ? sources : ["minimal"],
  });

  return {
    structuredLines,
    narrativeHtml,
    narrativeText,
    showSoftNotice,
    sources,
  };
}
